/**
 * Reduce a formatted delivery address to just its street line.
 *
 * Google returns a full formatted address — "193 Glengarry Road, Glen Eden,
 * Auckland 0602, New Zealand" — which is far too long for a header row that also
 * has to hold fulfilment pills, a time chip and the account slot. Design's rule:
 * street line only, never suburb, city or postcode. The full address stays
 * available in the sheet the store link opens, so nothing is hidden from the
 * customer, it is just not shouted on every screen.
 *
 * Unit and level prefixes are part of the street line and are kept — "Apartment
 * 12B, 148 Marine Parade" is one line, not two — because dropping them would
 * name the wrong door.
 */

/**
 * A segment that is ONLY a unit/level designator, with no street in it — the
 * one case where the next segment has to be pulled in to name a street at all.
 *
 * Deliberately narrow. "2/14 Rosebank Rd" already contains its street, so it
 * must NOT absorb the following segment (that produced
 * "2/14 Rosebank Rd, Avondale"). Only a bare designator like "Apartment 12B",
 * "Unit 4", "Level 3" or "#12" is incomplete on its own.
 */
const BARE_UNIT =
  /^(?:(?:apt|apartment|unit|flat|level|lvl|suite|ste|shop|villa|room|rm)\.?\s+\S+|#\s*\S+)$/i;

export const streetLine = (formatted?: string | null): string | undefined => {
  const value = formatted?.trim();
  if (!value) return undefined;

  const parts = value
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return parts[0];

  // A leading unit/level segment belongs with the street that follows it.
  if (BARE_UNIT.test(parts[0]) && parts.length > 1) {
    return `${parts[0]}, ${parts[1]}`;
  }

  return parts[0];
};

/**
 * Truncate for display. Design puts the cut at ~28 characters, before the
 * chevron, so the affordance to change the address never disappears with it.
 */
export const truncateStreet = (
  value: string | undefined,
  max = 28,
): string | undefined => {
  if (!value) return value;
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
};
